import { createAsyncThunk } from "@reduxjs/toolkit";
import { captureException } from "@sentry/browser";

import * as financingAPI from "../../../api/financing";
import { FinancingPlan } from "../../../models/financing";
import { IProductID } from "../../../models/nominals";
// eslint-disable-next-line import/no-restricted-paths -- pre-existing cross-app dependency (Phase E exemption)
import { filterFinancingPlans, sortFinancingPlans } from "../../checkout/utils";

/**
 * Load financing plans available for a specific product.
 *
 * This thunk fetches the available financing options for a product from the API,
 * filters them to show only relevant plans, and sorts them by APR and term length.
 * The filtering logic removes higher-APR plans if zero-percent financing is available.
 *
 * Error handling is graceful - if the API call fails, the error is captured in Sentry
 * but an empty array is returned so the configurator can continue functioning without
 * financing options.
 *
 * @param {Object} params - The parameters object
 * @param {IProductID} params.productID - The product to get financing plans for
 *
 * @returns {Promise<FinancingPlan[]>} Promise resolving to filtered and sorted financing plans
 *
 * @example
 * // Load financing plans when product is selected
 * dispatch(loadFinancingPlans({
 *   productID: 123
 * }));
 *
 * @sideEffects
 * - Updates `entities.financingPlans` with the fetched plans via extraReducers
 * - Captures exceptions to Sentry if the API call fails
 */
export const loadFinancingPlans = createAsyncThunk(
    "configurator/loadFinancingPlans",
    async ({
        productID,
    }: {
        productID: IProductID;
    }): Promise<FinancingPlan[]> => {
        // Load Financing plans depending on the product, but allow this to fail gracefully
        const financingPlans = await financingAPI
            .listFinancingPlansForProduct(productID)
            .catch((err) => {
                captureException(err);
                return [];
            });
        const sortedPlans = sortFinancingPlans(
            filterFinancingPlans(financingPlans),
        );
        return sortedPlans;
    },
);
